import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Rating,
  Button,
  TextField,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  MenuItem,
  Select,
  Pagination,
} from "@mui/material";
import {
  Verified,
  PhotoCamera,
  Star,
  StarBorder,
} from "@mui/icons-material";
import { getProductReviews, createReview } from "@/api/reviews";
/* ─── colour tokens ─── */
const C = {
  bg: '#fff',
  cardBg: '#fafafa',
  accent: '#fbbf24',
  accentDark: '#f59e0b',
  text: '#0f172a',
  textSec: '#475569',
  textMuted: '#94a3b8',
  border: '#f1f5f9',
  borderMed: '#e2e8f0',
  green: '#16a34a',
  blue: '#2563eb',
  blueBg: '#eff6ff',
  blueBorder: '#dbeafe',
};

/* ─── spacing scale ─── */
const S = { xs: 4, sm: 8, md: 12, base: 16, lg: 24, xl: 32, xxl: 48 };

const REVIEWS_PER_PAGE = 10;

/** Build summary + histogram from the review list (single API instead of stats + list). */
function buildStatsFromReviews(reviews) {
  const list = Array.isArray(reviews) ? reviews : [];
  const reviewCount = list.length;
  const buckets = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let sum = 0;
  for (const r of list) {
    const raw = Number(r.rating);
    if (!Number.isFinite(raw)) continue;
    sum += raw;
    const key = Math.min(5, Math.max(1, Math.round(raw)));
    buckets[key] += 1;
  }
  const avgRating = reviewCount ? sum / reviewCount : 0;
  const stats = [5, 4, 3, 2, 1].map((rating) => ({ rating, count: buckets[rating] || 0 }));
  return { avgRating, reviewCount, stats };
}

/* ─── skeleton review card ─── */
const SkeletonReview = () => (
  <div style={{ display: 'flex', gap: S.base, padding: `${S.lg}px 0`, borderBottom: `1px solid ${C.border}` }}>
    <Skeleton variant="circular" width={40} height={40} />
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: S.sm }}>
        <Skeleton width={120} height={18} />
        <Skeleton width={80} height={14} />
      </div>
      <Skeleton width={100} height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="100%" height={14} />
      <Skeleton width="80%" height={14} />
      <Skeleton width="60%" height={14} />
    </div>
  </div>
);

const ReviewSystem = ({ productId, onReviewMetaChange }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [filterStar, setFilterStar] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [newReview, setNewReview] = useState({ rating: 5, title: "", comment: "", images: [] });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const reviewsData = await getProductReviews(productId);
      setReviews(reviewsData);
      const statsData = buildStatsFromReviews(reviewsData);
      setStats(statsData);
      onReviewMetaChange?.({
        avgRating: statsData.avgRating,
        reviewCount: statsData.reviewCount,
      });
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
      setReviews([]);
      const empty = buildStatsFromReviews([]);
      setStats(empty);
      onReviewMetaChange?.({ avgRating: 0, reviewCount: 0 });
    } finally {
      setLoading(false);
    }
  }, [productId, onReviewMetaChange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setListPage(1);
  }, [productId, filterStar, sortBy, reviews.length]);


  const handleSubmit = async () => {
    if (!newReview.comment) return alert("Please add a comment");
    if (!newReview.name?.trim()) return alert("Please enter your name");
    try {
      setSubmitting(true);
      await createReview({ ...newReview, productId });
      setIsModalOpen(false);
      setNewReview({ rating: 5, name: "", comment: "" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to submit review");
    } finally { setSubmitting(false); }
  };

  /* derived */
  const totalReviews = stats?.reviewCount || 0;
  const avgRating = Number(stats?.avgRating) || 0;
  const recommendPct = totalReviews > 0
    ? Math.round(((stats?.stats?.filter(s => s.rating >= 4).reduce((a, b) => a + b.count, 0) || 0) / totalReviews) * 100)
    : 0;

  const filteredReviews = useMemo(() => {
    let list = [...reviews];
    if (filterStar > 0) list = list.filter(r => r.rating === filterStar);
    if (sortBy === 'recent') list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    else if (sortBy === 'highest') list.sort((a, b) => b.rating - a.rating);
    else if (sortBy === 'lowest') list.sort((a, b) => a.rating - b.rating);
    return list;
  }, [reviews, filterStar, sortBy]);

  const reviewListPageCount = Math.max(1, Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE));

  const pagedReviews = useMemo(() => {
    const start = (listPage - 1) * REVIEWS_PER_PAGE;
    return filteredReviews.slice(start, start + REVIEWS_PER_PAGE);
  }, [filteredReviews, listPage]);

  const reviewImages = useMemo(() => reviews.flatMap(r => (r.images || []).map(img => ({ img, reviewId: r._id }))), [reviews]);

  useEffect(() => {
    setListPage((prev) => Math.min(prev, reviewListPageCount));
  }, [reviewListPageCount]);

  /* ─── LOADING STATE ─── */
  if (loading) return (
    <div style={{ marginTop: S.xxl, paddingTop: S.xxl, borderTop: `1px solid ${C.border}` }}>
      <Skeleton width={200} height={36} sx={{ mb: 3 }} />
      <Skeleton variant="rounded" width="100%" height={180} sx={{ borderRadius: '20px', mb: 3 }} />
      <SkeletonReview />
      <SkeletonReview />
    </div>
  );

  /* ─── RENDER ─── */
  return (
    <div style={{ marginTop: S.xl, paddingTop: S.lg, borderTop: `1px solid ${C.border}` }}>

      {/* ═══ Section Title ═══ */}
      <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: S.base, letterSpacing: '-0.3px' }}>
        Customer Reviews
      </h2>

      {/* ═══ Rating Summary Card ═══ */}
      <div style={{
        background: C.cardBg,
        borderRadius: 16,
        border: `1px solid ${C.border}`,
        padding: `${S.lg}px ${S.lg}px`,
        marginBottom: S.lg,
        maxWidth: 520,
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: S.lg, alignItems: 'center' }}>
          {/* Left: Big Score */}
          <div style={{ textAlign: 'center', minWidth: 100 }}>
            <div style={{ fontSize: 56, fontWeight: 900, color: C.text, lineHeight: 1, letterSpacing: '-2px' }}>
              {avgRating.toFixed(1)}
            </div>
            <Rating value={avgRating} precision={0.1} readOnly size="small" sx={{ color: C.accent, mt: 0.5 }} />
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2, fontWeight: 500 }}>
              {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
            </div>
            {totalReviews > 0 && (
              <div style={{ fontSize: 11, color: C.green, fontWeight: 700, marginTop: 4 }}>
                {recommendPct}% recommend
              </div>
            )}
          </div>

          {/* Middle: Rating Bars */}
          <div style={{ flex: 1, minWidth: 200, maxWidth: 400 }}>
            {(stats?.stats || []).map((row) => {
              const pct = totalReviews ? (row.count / totalReviews) * 100 : 0;
              const isActive = filterStar === row.rating;
              return (
                <div
                  key={row.rating}
                  onClick={() => setFilterStar(isActive ? 0 : row.rating)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                    cursor: 'pointer', padding: '3px 6px', borderRadius: 6,
                    background: isActive ? C.blueBg : 'transparent',
                    transition: 'background 0.2s',
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text, width: 10, textAlign: 'right' }}>{row.rating}</span>
                  <Star sx={{ fontSize: 12, color: C.accent }} />
                  <div style={{
                    flex: 1, height: 8, background: '#eef2f6', borderRadius: 4, overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: 4,
                      background: `linear-gradient(90deg, ${C.accent}, ${C.accentDark})`,
                      width: `${pct}%`,
                      transition: 'width 0.6s cubic-bezier(.4,0,.2,1)',
                    }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, width: 20, textAlign: 'right' }}>{row.count}</span>
                </div>
              );
            })}
          </div>

          {/* Right: CTA (Only if reviews exist) */}
          {totalReviews > 0 && (
            <div style={{ marginLeft: 'auto' }}>
              <Button
                variant="contained"
                onClick={() => setIsModalOpen(true)}
                sx={{
                  textTransform: 'none', fontWeight: 700, fontSize: 14, color: '#fff',
                  background: C.text, borderRadius: '12px', height: 44, px: 3,
                  boxShadow: '0 4px 14px rgba(15,23,42,0.15)',
                  '&:hover': { background: '#1e293b', boxShadow: '0 6px 20px rgba(15,23,42,0.2)' },
                }}
              >
                Write a Review
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ═══ Customer Photos ═══ */}
      {reviewImages.length > 0 && (
        <div style={{ marginBottom: S.xl }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: S.base }}>Customer Photos</h3>
          <div style={{ display: 'flex', gap: S.sm, overflowX: 'auto', paddingBottom: S.sm }}>
            {reviewImages.slice(0, 8).map((item, i) => (
              <img key={i} src={item.img} alt="Customer" style={{
                width: 72, height: 72, objectFit: 'cover', borderRadius: 12,
                border: `2px solid ${C.bg}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flexShrink: 0,
                cursor: 'pointer', transition: 'transform 0.2s',
              }} />
            ))}
          </div>
        </div>
      )}

      {/* ═══ Toolbar: Filter + Sort ═══ */}
      <div
        id="customer-reviews-list"
        style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
        gap: S.sm, marginBottom: S.base, paddingBottom: S.md, borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: S.sm, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>
            {filterStar > 0 ? `${filteredReviews.length} of ${totalReviews}` : totalReviews} {totalReviews === 1 ? 'Review' : 'Reviews'}
          </h3>
          {filterStar > 0 && (
            <button
              onClick={() => setFilterStar(0)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 12, fontWeight: 600, color: C.blue, background: C.blueBg,
                border: `1px solid ${C.blueBorder}`, borderRadius: 16, padding: '4px 12px',
                cursor: 'pointer',
              }}
            >
              {filterStar} stars ✕
            </button>
          )}
        </div>
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          size="small"
          sx={{
            fontSize: 13, fontWeight: 600, borderRadius: '10px',
            '.MuiOutlinedInput-notchedOutline': { borderColor: C.borderMed },
          }}
        >
          <MenuItem value="recent">Most Recent</MenuItem>
          <MenuItem value="highest">Highest Rated</MenuItem>
          <MenuItem value="lowest">Lowest Rated</MenuItem>
        </Select>
      </div>

      {/* ═══ Reviews List ═══ */}
      {filteredReviews.length === 0 && !loading ? (
        /* ─── Empty State ─── */
        <div style={{
          textAlign: 'center', padding: `${S.xl}px ${S.lg}px`,
          background: C.cardBg, borderRadius: 16, border: `2px dashed ${C.borderMed}`,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, background: C.bg,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: S.base,
          }}>
            <StarBorder sx={{ fontSize: 28, color: C.textMuted }} />
          </div>
          <h4 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: `0 0 ${S.sm}px` }}>
            {filterStar > 0 ? `No ${filterStar}-star reviews` : 'No reviews yet'}
          </h4>
          <p style={{ fontSize: 14, color: C.textSec, maxWidth: 280, margin: '0 auto', lineHeight: 1.6 }}>
            {filterStar > 0
              ? 'Try clearing filters to see all reviews.'
              : 'Help others by sharing your experience.'}
          </p>
          {filterStar > 0 ? (
            <Button
              onClick={() => setFilterStar(0)}
              sx={{ mt: 2, textTransform: 'none', fontWeight: 600, color: C.blue }}
            >
              Clear Filter
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => setIsModalOpen(true)}
              sx={{
                mt: 2.5, textTransform: 'none', fontWeight: 700, color: '#fff',
                background: C.text, borderRadius: '12px', height: 44, px: 3,
                '&:hover': { background: '#1e293b' },
              }}
            >
              Write a Review
            </Button>
          )}
        </div>
      ) : (
        <div>
          {pagedReviews.map((review) => (
            <div
              key={review._id}
              style={{
                padding: `${S.lg}px 0`, borderBottom: `1px solid ${C.border}`,
                transition: 'background 0.2s',
              }}
            >
              {/* Header row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: S.md }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: S.md }}>
                  <Avatar sx={{ bgcolor: C.text, width: 40, height: 40, fontSize: 15, fontWeight: 700 }}>
                    {(review.name || review.userId?.name || "A").charAt(0).toUpperCase()}
                  </Avatar>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: S.sm }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                        {review.name || review.userId?.name || "Anonymous"}
                      </span>
                      {review.isVerified && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: 10, fontWeight: 800, color: C.green,
                          background: '#f0fdf4', borderRadius: 6, padding: '2px 8px',
                          textTransform: 'uppercase', letterSpacing: 0.4,
                        }}>
                          <Verified sx={{ fontSize: 11 }} /> Verified
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 12, color: C.textMuted }}>
                      {new Date(review.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stars + Title */}
              <div style={{ display: 'flex', alignItems: 'center', gap: S.sm, marginBottom: S.sm }}>
                <Rating value={review.rating} readOnly size="small" sx={{ color: C.accent }} />
                {review.title && (
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{review.title}</span>
                )}
              </div>

              {/* Body */}
              <p style={{ fontSize: 14, color: C.textSec, lineHeight: 1.7, margin: `0 0 ${S.base}px` }}>
                {review.comment}
              </p>

              {/* Review images */}
              {review.images?.length > 0 && (
                <div style={{ display: 'flex', gap: S.sm, marginBottom: S.base, overflowX: 'auto' }}>
                  {review.images.map((img, i) => (
                    <img key={i} src={img} alt="" style={{
                      width: 72, height: 72, objectFit: 'cover', borderRadius: 10,
                      border: `1px solid ${C.borderMed}`, flexShrink: 0,
                    }} />
                  ))}
                </div>
              )}

            </div>
          ))}
          {filteredReviews.length > REVIEWS_PER_PAGE && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: S.lg, paddingBottom: S.md }}>
              <Pagination
                count={reviewListPageCount}
                page={listPage}
                onChange={(_, v) => {
                  setListPage(v);
                  document.getElementById("customer-reviews-list")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                color="primary"
                shape="rounded"
                size="small"
              />
            </div>
          )}
        </div>
      )}

      {/* ═══ Write Review Modal ═══ */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: 22, pt: 3, px: 3, color: C.text }}>
          Write a Review
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.textSec, marginBottom: S.sm }}>Rating</p>
            <Rating
              value={newReview.rating}
              onChange={(_, val) => setNewReview(prev => ({ ...prev, rating: val }))}
              size="large"
              sx={{ color: C.accent, fontSize: '2.2rem' }}
            />
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.textSec, marginBottom: S.sm }}>Your Name</p>
            <TextField
              fullWidth variant="outlined" size="small"
              value={newReview.name || ''}
              onChange={(e) => setNewReview(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter your name"
            />
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.textSec, marginBottom: S.sm }}>Your Review</p>
            <TextField
              fullWidth multiline rows={4} variant="outlined" size="small"
              value={newReview.comment}
              onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="What did you like or dislike?"
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setIsModalOpen(false)} sx={{ textTransform: 'none', color: C.textMuted, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit} variant="contained" disabled={submitting}
            sx={{
              textTransform: 'none', fontWeight: 700, color: '#fff',
              background: C.text, borderRadius: '12px', px: 4, py: 1.2,
              '&:hover': { background: '#1e293b' },
            }}
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReviewSystem;
